import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { useTranslation } from '@/hooks/useTranslation';
import { AlertCircle, RefreshCw } from 'lucide-react';
import React from 'react';

interface ErrorStateProps {
  /**
   * The error object or message to display
   */
  error: unknown;

  /**
   * Callback function to retry the failed operation
   */
  onRetry: () => void;

  /**
   * Optional title for the error state. If provided, will show page title section.
   * Translation key for the title text.
   */
  title?: string;

  /**
   * Optional subtitle for the error state. Only shown if title is provided.
   * Translation key for the subtitle text.
   */
  subtitle?: string;

  /**
   * Translation key for the alert title. Defaults to 'error'
   */
  alertTitle?: string;

  /**
   * Translation key for the default error message. Defaults to 'messages.loadError'
   */
  defaultErrorMessage?: string;

  /**
   * Translation key for the retry button text. Defaults to 'tryAgain'
   */
  retryButtonText?: string;

  /**
   * Additional props to pass to the subtitle translation function
   */
  subtitleProps?: Record<string, unknown>;

  /**
   * Whether to show the retry button. Defaults to true.
   */
  showRetryButton?: boolean;
}

/**
 * Generic ErrorState component for displaying error states with optional retry functionality
 *
 * @example
 * // Basic usage without title
 * <ErrorState error={error} onRetry={handleRetry} />
 *
 * @example
 * // With custom title and subtitle
 * <ErrorState
 *   error={error}
 *   onRetry={handleRetry}
 *   title="users.title"
 *   subtitle="users.subtitle"
 *   subtitleProps={{ count: 0 }}
 * />
 *
 * @example
 * // With custom error messages
 * <ErrorState
 *   error={error}
 *   onRetry={handleRetry}
 *   alertTitle="users.error"
 *   defaultErrorMessage="users.messages.loadError"
 *   retryButtonText="users.tryAgain"
 * />
 */
export const ErrorState: React.FC<ErrorStateProps> = React.memo(
  ({
    error,
    onRetry,
    title,
    subtitle,
    alertTitle = 'error',
    defaultErrorMessage = 'messages.loadError',
    retryButtonText = 'tryAgain',
    subtitleProps,
    showRetryButton = true,
  }) => {
    const { tDynamic: t } = useTranslation();

    return (
      <div className='space-y-6'>
        {title && (
          <div className='mb-6'>
            <h1 className='text-3xl font-bold'>{t(title)}</h1>
            {subtitle && (
              <p className='text-muted-foreground'>
                {t(subtitle, subtitleProps)}
              </p>
            )}
          </div>
        )}

        <Alert variant='destructive'>
          <AlertCircle className='h-4 w-4' />
          <AlertTitle>{t(alertTitle)}</AlertTitle>
          <AlertDescription>
            {error instanceof Error ? error.message : t(defaultErrorMessage)}
          </AlertDescription>
        </Alert>

        {showRetryButton && (
          <Button onClick={onRetry} variant='outline'>
            <RefreshCw className='mr-2 h-4 w-4' />
            {t(retryButtonText)}
          </Button>
        )}
      </div>
    );
  }
);

ErrorState.displayName = 'ErrorState';

export default ErrorState;
